/**
 * Formats a person's full name from separate firstName and lastName fields.
 * @param firstName Optional first name
 * @param lastName Optional last name
 * @param fallback Value to return when both names are empty (defaults to empty string)
 * @returns Formatted full name, or the fallback value if both names are absent
 */
export function formatPersonName(
  firstName?: string | null,
  lastName?: string | null,
  fallback = '',
): string {
  const fullName = [firstName?.trim(), lastName?.trim()].filter(Boolean).join(' ');
  return fullName || fallback;
}

/**
 * Formats a user's display name
 * @param user User object with optional firstName, lastName, and required email. Accepts null or undefined.
 * @returns Formatted full name, or email if name is not available, or empty string if user is null/undefined
 */
export function formatUserFullName(user: {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
} | null | undefined): string {
  if (!user) return '';
  return formatPersonName(user.firstName, user.lastName) || user.email;
}

/**
 * Formats a user's display name with a specified name order.
 * @param user User object with optional firstName, lastName, and required email. Accepts null or undefined.
 * @param nameFormat 'firstLast' for "Prénom Nom" (default), 'lastFirst' for "NOM, Prénom"
 * @returns Formatted name according to the specified format
 */
export function formatUserDisplayName(
  user: { firstName?: string | null; lastName?: string | null; email: string } | null | undefined,
  nameFormat: 'firstLast' | 'lastFirst' = 'firstLast',
): string {
  if (!user) return '';
  if (nameFormat === 'lastFirst') {
    const first = user.firstName?.trim() || '';
    const last = user.lastName?.trim() || '';
    return last && first ? `${last.toUpperCase()}, ${first}` : last || first;
  }
  return formatUserFullName(user);
}

/**
 * A name shortened to one term plus the other's initial — « Marie-Pierre T. » or
 * « Tremblay, M. ». For a cell too narrow to hold both terms, where the term kept has to be
 * the one the list is ordered by: the eye looks for what it sorted on (#814).
 *
 * Lives here rather than beside its caller because UI-CHARTE.md §3 makes this file the only
 * home for person names. Falls back on whichever term exists when the other is missing, and
 * on the email when neither does.
 */
export function formatUserInitialName(
  user: { firstName?: string | null; lastName?: string | null; email?: string | null } | null | undefined,
  keep: 'first' | 'last' = 'first',
): string {
  if (!user) return '';
  const first = user.firstName?.trim() || '';
  const last = user.lastName?.trim() || '';
  if (!first && !last) return user.email?.trim() || '';
  if (!first) return last;
  if (!last) return first;
  return keep === 'first'
    ? `${first} ${last.charAt(0).toUpperCase()}.`
    : `${last}, ${first.charAt(0).toUpperCase()}.`;
}

